import { API_BASE_URL } from '@/config/api-url';
import { resolveAppGuid } from '@/utils/appGuid';
import { isCustomDomain } from '@/utils/publicHost';
import { publicTraceHeaders } from '@/public/publicTrace';
import { visitorLanguageQuery } from '@/utils/visitorLanguage';
import { smartLinkVisitorQuery } from '@/utils/smartLinkVisitorQuery';

export interface ResolvedHostTarget {
  /**
   * WEB-395 (re-bounce 2026-07-06): 'redirect' covers both store-fallback
   * (paused/inactive/broken funnel or piece on a bound domain, falling back
   * to the project's iosStoreUrl/androidStoreUrl) and smart-link URL-kind
   * directions — the backend already picked the destination; targetId holds
   * the URL to navigate to. slug is '' for this targetType (mirrors
   * PublicRenderController's REDIRECT response shape).
   */
  readonly targetType: 'quiz' | 'paywall' | 'redirect';
  readonly targetId: string;
  readonly projectId: string;
  readonly slug: string;
  /**
   * WEB-1482: пейволл, ВЫБРАННЫЙ направлением A/B-теста или маршрутом
   * воронки, — им квиз обязан заменить пейволл, подобранный сервером по
   * эвристике «самая новая активная воронка на этот квиз».
   *
   * Именно СЛАГ, а не идентификатор: публичная выдача пейволла адресуется
   * слагом (`/public/paywall/by-id/:projectId/:paywallSlug`), по UUID она
   * отвечает 404. Сервер кладёт это поле, только если пейволл того же
   * проекта и в состоянии ACTIVE, — поэтому «поле есть» здесь означает
   * «этот пейволл можно показывать». Поля нет → прежний запасной путь
   * (пейволл воронки), НИКОГДА не подстановка paywallOverrideId вместо
   * слага: это дало бы посетителю 404 вместо рабочего экрана оплаты.
   */
  readonly paywallOverrideSlug?: string;
  /**
   * WEB-1989 шаг 1 — ГОТОВОЕ тело показа (то же, что отдаёт `by-id`, поле в
   * поле: перевод по языку, `geo_country` по стране запроса, контекст воронки
   * у квиза). Есть тело — страница показа НЕ идёт за ним вторым запросом.
   *
   * Поля может не быть: сервер не смог собрать (тогда он честно не кладёт
   * ключ) либо отвечает старая версия бэка. Оба случая означают одно —
   * прежний путь в два запроса, а не белый экран.
   */
  readonly publicContent?: Record<string, unknown>;
  /**
   * WEB-1989 шаг 2 — ответ пришёл не от бэка, а из последней хорошей копии
   * воркера на краю (бэк не ответил или ответил 5xx). Показать воронку по
   * копии можно, а вот принять деньги — нельзя: оплата идёт в живой API.
   * Пейволл по этому признаку рисует честную плашку вместо кнопки, которая
   * всё равно не сработает.
   */
  readonly servedStale: boolean;
  /**
   * WEB-3673 (путь денег) — «показ этого ответа засчитывается платным
   * запуском» (не портал по исключению тарифного гейта). Страница,
   * загруженная с `?preload=1`, шлёт подтверждение показа
   * (`public/showConfirm.ts`) ТОЛЬКО при этом признаке; без `preload` запуск
   * засчитал сам резолв, и признак странице не нужен. Ключ есть, только когда
   * сервер прислал ровно `true`.
   */
  readonly showConfirm?: true;
}

/**
 * WEB-3673: загружена ли страница прогревом SDK — ровно одно значение
 * `preload=1` в строке параметров. Одна дверь для запроса резолва и для
 * `PublicHostRouter` (слать ли подтверждение показа); та же форма у сервера
 * (`isPreloadRequest`). Признак иной формы — обычный заход: резолв считает
 * запуск, подтверждение не шлётся.
 */
export function isPreloadSearch(search: string | null | undefined): boolean {
  if (!search) return false;
  const values = new URLSearchParams(search).getAll('preload');
  return values.length === 1 && values[0] === '1';
}

/** Заголовок-метка воркера (см. infra/cloudflare/w2a-funnel-router.js). */
const STALE_HEADER = 'x-w2a-stale';

/**
 * Ревизия пачки 29, п.2 — признак «ответ действительно от резолва». Ставят оба
 * конца: сервер (`backend/src/domains/middleware/host-context.middleware.ts`,
 * до обработчика — значит и на 404/403) и воркер
 * (`infra/cloudflare/w2a-funnel-router.js`, RESOLVE_HEADER — на свежий ответ и
 * на копию, но НЕ на собственный 503).
 */
const RESOLVE_HEADER = 'x-w2a-resolve';

/**
 * Ревизия пачки 29, п.2(а) — потолок ожидания КАЖДОГО запроса резолва.
 *
 * Потолка не было вовсе: зависший запрос (мобильная сеть; ответ, который не
 * придёт никогда) держал страницу показа в спиннере столько, сколько решит
 * браузер, и запасной адрес не пробовался НИ РАЗУ.
 *
 * Почему 8 секунд. Собственный потолок воркера у бэка — 3 с
 * (ORIGIN_TIMEOUT_MS), после него он отвечает копией или своим 503. Значит
 * относительный запрос, не ответивший за 8 с, означает не «бэк думает», а
 * «до края не достучались»: 8 с оставляют запас на установку соединения и
 * TLS на медленной мобильной сети поверх этих 3 с и при этом заметно ниже
 * порога, за которым посетитель уходит сам.
 *
 * Потолок ПОРАЗРЯДНЫЙ (у каждого адреса свой), а не общий на оба: общий
 * бюджет отдал бы запасному адресу остаток времени — то есть в худшем случае
 * ноль, и запасной путь снова оказался бы недостижим ровно в тот момент,
 * ради которого он заведён. Принятая цена — в худшем случае два потолка
 * подряд; она уплачена только там, где альтернатива — гарантированное
 * «Not found.» на платном трафике.
 */
export const RESOLVE_TIMEOUT_MS = 8000;

/**
 * Один поход за резолвом с потолком ожидания.
 *
 * Возвращает `null` на любом «ответа нет» (обрыв сети, отмена по потолку) —
 * для вызывающего это не отказ сервера, а повод попробовать следующий адрес.
 */
async function fetchResolveWithTimeout(url: string): Promise<Response | null> {
  const controller =
    typeof AbortController === 'function' ? new AbortController() : null;
  const timer = setTimeout(() => controller?.abort(), RESOLVE_TIMEOUT_MS);
  try {
    // Починка ревизии (личность посетителя резолва vs остальных публичных
    // запросов): АДРЕС ОПРЕДЕЛЯЕТ, чья это кука.
    //
    // Относительный адрес (`/public/resolve...`, WEB-1989 шаг 2) уходит на
    // САМ клиентский домен — его принимает воркер того же домена, то есть
    // это same-site запрос, и `qb_visitor_id` (SameSite=Lax) реально
    // доезжает до сервера при credentials:'include'. Middleware
    // (`host-context.middleware.ts`) берёт куку раньше `?visitorId` —
    // а СОБЫТИЯ/пейволл/чекаут идут абсолютным адресом API межсайтово, кука
    // там браузером не отправляется вовсе, и сервер берёт visitorId только
    // из тела/квери. Разные адреса — разные личности одного посетителя, если
    // кука и localStorage разошлись (Safari чистит localStorage раз в 7
    // дней; заход из приложения с ?origin=app&guid=). Починка возвращает
    // прежнюю (до WEB-1989) семантику: у резолва на клиентском домене куки
    // нет — личность одна, `?visitorId`, как у всех остальных запросов.
    //
    // WEB-263: абсолютный адрес (запасной путь этой же функции, и
    // единственный на наших /public/*-поддоменах без воркера) остаётся с
    // credentials:'include' — он межсайтовый, поэтому SameSite=Lax куку
    // браузер сюда и так не приложит; исторически include стоял ради этого
    // адреса, менять тут нечего.
    const isRelative = url.startsWith('/');
    return await fetch(url, {
      credentials: isRelative ? 'omit' : 'include',
      // WEB-1991 (возврат приёмки 13.09): заголовок трейса на САМОМ первом
      // запросе. Инструментовку `fetch` ставит SDK, а он грузится лениво уже
      // после старта приложения — резолв уходит раньше и оставался вне
      // трейса. Значение берётся у тонкого предзагрузчика
      // (`public/publicTrace.ts`), тот же трейс продолжит SDK, когда
      // догрузится. Без DSN объект пустой — ни заголовка, ни лишнего
      // предварительного запроса CORS.
      //
      // Оба адреса здесь — НАШИ (относительный путь клиентского домена и наш
      // API); чужим доменам заголовок не уходит по построению.
      headers: publicTraceHeaders(),
      ...(controller ? { signal: controller.signal } : {}),
    });
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * WEB-2024 — честный отказ резолва С КОДОМ реестра сервера (сегодня —
 * `PUBLIC_FUNNEL_UNAVAILABLE`: воронка клиента на FREE или заперта
 * администратором). Отличается от `null` («домена нет»): страница показывает
 * покупателю «страница недоступна» на его языке, а не «не найдено».
 */
export interface PublicResolveRefusal {
  readonly refused: true;
  readonly code: string;
}

export function isPublicResolveRefusal(
  value: ResolvedHostTarget | PublicResolveRefusal | null,
): value is PublicResolveRefusal {
  return value !== null && 'refused' in value && value.refused === true;
}

async function readRefusalCode(attempt: Response): Promise<string | null> {
  try {
    const body = (await attempt.json()) as { code?: unknown } | null;
    return body && typeof body.code === 'string' && body.code
      ? body.code
      : null;
  } catch {
    return null;
  }
}

/**
 * Calls `GET /public/resolve` and returns the slug-level identifiers needed
 * to route to the existing PublicQuiz / PublicPaywall renderers, OR a
 * 'redirect' target (store-fallback / smart-link URL direction) for the
 * caller to navigate the browser to.
 *
 * Returns null on 404 (no domain bound to this host/path, or bound but with
 * no servable content and no store fallback available) or network failure.
 * The renderer treats null as "show the not-found page".
 */
export async function resolvePublicHost(
  host: string,
  path: string,
  // WEB-1989: язык посетителя — тот же, что страница показа послала бы в
  // `by-id` (`resolveVisitorLanguages`). Без него сервер соберёт тело на языке
  // самой воронки, и посетитель получил бы НЕ тот язык за один запрос вместо
  // правильного за два. null/пусто = не передаём (прежнее поведение сервера).
  // WEB-2471 п.2: список — весь перечень языков браузера по порядку, уходит
  // как `lang=<первый>&langs=<все>` (visitorLanguageQuery); ключ копии этого
  // ответа на краю строится по обоим параметрам.
  lang: string | readonly string[] | null = null,
  // WEB-2783: строка параметров адресной строки посетителя. Серверу уходят
  // ТОЛЬКО метки кампании и клика из белого списка (`smartLinkVisitorQuery`)
  // — из них сервер собирает метки внешнего адреса направления умной ссылки.
  // Ключ копии ответа на краю метки не включает, а ответ-переадресацию край в
  // копию не кладёт (`classifyResponse`, сторож w2a-funnel-router-smartlink-
  // marks.web2783.spec.ts) — клик одного посетителя другому не достанется.
  //
  // По умолчанию — адресная строка самой страницы: так проводка не стоит
  // байтов первого экрана у вызывающего (роутер зовёт резолв без неё).
  visitorSearch: string | null = window.location.search,
): Promise<ResolvedHostTarget | PublicResolveRefusal | null> {
  const langQuery = visitorLanguageQuery(
    typeof lang === 'string' ? [lang] : (lang ?? []),
  );
  try {
    // WEB-1257 (хвост WEB-1403, cto-duo «гибрид» 2026-08-16): на клиентском
    // домене кука qb_visitor_id межсайтовая (SameSite=Lax) и браузером не
    // сохраняется НИКОГДА — сервер выпускал новый случайный ключ на каждый
    // показ, вариант A/B перевыбирался на каждую перезагрузку. Поэтому в
    // квери едет стабильный клиентский id — ТЕМ ЖЕ резолвом, что у событий
    // (trackEvent.ts) и оплаты (PaywallScreenWizard): app-guid из
    // ?origin=app&guid=X, иначе localStorage visitor-id. Кука главнее на
    // сервере (WEB-253) — на наших поддоменах ничего не меняется.
    // WEB-3673 (путь денег): прогрев страницы мобильным SDK (`?preload=1`)
    // уходит серверу как «отдай содержимое, запуск не считай» — его засчитает
    // подтверждение показа. Ровно форма `preload=1` (одно значение): её и
    // только её сервер читает как прогрев. Ключ копии ответа на краю этот
    // признак не включает (infra/cloudflare/w2a-funnel-router.js,
    // buildCacheKeyUrl), а фоновая перепроверка копии уходит к серверу полным
    // адресом — с тем же признаком.
    const query = `?host=${encodeURIComponent(
      host,
    )}&path=${encodeURIComponent(path)}&visitorId=${encodeURIComponent(
      resolveAppGuid(),
    )}${smartLinkVisitorQuery(visitorSearch)}${langQuery ? `&${langQuery}` : ''}${
      isPreloadSearch(visitorSearch) ? '&preload=1' : ''
    }`;

    // WEB-1989 шаг 2 — ПОРЯДОК АДРЕСОВ.
    //
    // Воркер клиентского домена (infra/cloudflare/w2a-funnel-router.js) держит
    // кеш этого ответа на краю и отдаёт последнюю хорошую копию, когда наш API
    // молчит. Но привязан он к маршрутам `<домен>/*` — то есть видит ТОЛЬКО
    // запросы на сам клиентский домен. Абсолютный адрес API (`VITE_API_URL`)
    // проходит мимо него, и никакого кеша на краю у такого запроса нет.
    // Поэтому на клиентском домене первым идёт ОТНОСИТЕЛЬНЫЙ адрес.
    //
    // Второй адрес — прежний абсолютный — остаётся ЗАПАСНЫМ, и это не
    // перестраховка: пока воркер не выкачен, по относительному адресу
    // приезжает HTML страницы-оболочки (маршрут `<домен>/*` ведёт во фронт).
    // Без запасного пути выкат фронта раньше воркера погасил бы показ на всех
    // клиентских доменах. Цена запасного пути — один лишний запрос, и только
    // до выката воркера.
    const urls = isCustomDomain()
      ? [`/public/resolve${query}`, `${API_BASE_URL}/public/resolve${query}`]
      : [`${API_BASE_URL}/public/resolve${query}`];

    let res: Response | null = null;
    let data: Record<string, unknown> | null = null;
    for (const url of urls) {
      const attempt = await fetchResolveWithTimeout(url);
      // Ответа нет вовсе (обрыв, потолок ожидания) — пробуем следующий адрес;
      // следующего нет — «не найдено», как и раньше.
      if (!attempt) continue;
      if (!attempt.ok) {
        // Ревизия пачки 29, п.2: честным отказом считается ТОЛЬКО ответ с
        // признаком резолва (404 «домена нет», 403 тарифного гейта) — повтор
        // по второму адресу ничего не изменит, а показ замедлит.
        //
        // Без признака по этому адресу ответил НЕ резолв: 503 воркера без
        // копии, 404 чужого прокси перед CNAME-доменом, чей-то щит. Раньше
        // всё это считалось честным отказом и гасило показ при живом API —
        // теперь идём вторым, абсолютным адресом.
        if (attempt.headers?.get?.(RESOLVE_HEADER) === '1') {
          // WEB-2024: отказ гейта тарифа несёт код реестра
          // (`PUBLIC_FUNNEL_UNAVAILABLE`) — страница покажет покупателю текст
          // на его языке. Отказ без кода (домена нет) — по-прежнему null.
          const code = await readRefusalCode(attempt);
          return code ? { refused: true, code } : null;
        }
        continue;
      }
      try {
        data = (await attempt.json()) as Record<string, unknown>;
      } catch {
        // Не JSON — значит по этому адресу ответил не резолв (страница-
        // оболочка до выката воркера). Пробуем следующий адрес.
        data = null;
      }
      if (data) {
        res = attempt;
        break;
      }
    }
    if (!res || !data) return null;
    const targetType = data['targetType'];
    if (
      targetType !== 'quiz' &&
      targetType !== 'paywall' &&
      targetType !== 'redirect'
    ) {
      return null;
    }
    // WEB-1482: непустая строка — единственная форма, в которой поле имеет
    // смысл. Пустую строку/не-строку роняем в «переопределения нет», ключ при
    // этом не заводим вовсе, чтобы потребитель отличал «не пришло» от
    // «пришло пустым».
    const overrideSlug = data['paywallOverrideSlug'];
    const paywallOverrideSlug =
      typeof overrideSlug === 'string' && overrideSlug.length > 0
        ? overrideSlug
        : undefined;

    // WEB-1989: тело показа. Принимаем ТОЛЬКО объект — массив и строка тут
    // означали бы сломанный контракт, и лучше сходить вторым запросом, чем
    // отдать странице мусор вместо воронки.
    const rawContent = data['publicContent'];
    const publicContent =
      rawContent && typeof rawContent === 'object' && !Array.isArray(rawContent)
        ? (rawContent as Record<string, unknown>)
        : undefined;

    return {
      targetType,
      targetId: String(data['targetId'] ?? ''),
      projectId: String(data['projectId'] ?? ''),
      slug: String(data['slug'] ?? ''),
      // WEB-1989 шаг 2: метку ставит воркер на краю; на нашем поддомене
      // рендера воркера нет, поэтому там признак всегда false.
      servedStale: res.headers?.get?.(STALE_HEADER) === '1',
      ...(publicContent ? { publicContent } : {}),
      ...(paywallOverrideSlug ? { paywallOverrideSlug } : {}),
      // WEB-3673: только ровно `true` — иначе ключа нет.
      ...(data['showConfirm'] === true ? { showConfirm: true as const } : {}),
    };
  } catch {
    return null;
  }
}
